/**
 * 댓글 트리 탐색·딥링크 노출 계획 (JCG-3603 댓글 딥링크).
 *
 * 순수 함수만 두어 클라이언트·서버·테스트 어디서나 import 할 수 있다(DOM·React 상태 접근 없음).
 *
 * 세 댓글 섹션(`CommentSection`/`ForumCommentSection`/`RestorationCommentSection`)은 **루트 댓글에만**
 * 카테고리 탭 필터와 페이지네이션(`visibleCount`)을 적용하고, 답글은 부모 아래 중첩 렌더한다.
 * 따라서 답글이 대상이면 그 **루트 조상**이 보이게 만들면(탭 선택 + visibleCount 확장) 대상도 함께 렌더된다.
 *
 * 앵커 id·해시 파싱 규약은 `comment-links.ts`(공용 SSOT)가 담당하고, 이 모듈은 "어느 탭·몇 개까지
 * 보여야 대상이 화면에 존재하는가"만 계산한다.
 */
import type { Comment, CommentCategory } from "@/lib/types";

export interface CommentPath {
  /** 대상이 속한 루트 댓글 (대상이 루트면 대상 자신) */
  root: Comment;
  /** `roots` 배열 안에서의 루트 인덱스 (카테고리 필터 전, 0-base) */
  rootIndex: number;
  /** 루트부터 대상의 부모까지 위→아래 순서의 id 목록 (대상 자신 제외). 루트 대상이면 빈 배열 */
  ancestorIds: string[];
  /** 대상 댓글 */
  target: Comment;
}

interface SubtreeMatch {
  ancestorIds: string[];
  target: Comment;
}

function findInSubtree(
  node: Comment,
  targetId: string,
  ancestorIds: string[]
): SubtreeMatch | null {
  if (node.id === targetId) return { ancestorIds, target: node };
  if (!node.replies || node.replies.length === 0) return null;

  const nextAncestorIds = [...ancestorIds, node.id];
  for (const reply of node.replies) {
    const found = findInSubtree(reply, targetId, nextAncestorIds);
    if (found) return found;
  }
  return null;
}

/**
 * 루트 댓글 배열에서 `targetId` 를 깊이 우선으로 찾아 루트·조상 경로를 돌려준다. 없으면 `null`.
 * 삭제된(soft delete) 댓글도 트리에 남아 있으므로 그대로 찾는다.
 */
export function findCommentPath(roots: Comment[], targetId: string): CommentPath | null {
  for (let rootIndex = 0; rootIndex < roots.length; rootIndex++) {
    const root = roots[rootIndex];
    const found = findInSubtree(root, targetId, []);
    if (found) {
      return { root, rootIndex, ancestorIds: found.ancestorIds, target: found.target };
    }
  }
  return null;
}

/** `count` 를 `pageSize` 의 배수로 올림한다. `pageSize` 가 양수가 아니면 `count` 그대로. */
export function roundUpToPageSize(count: number, pageSize: number): number {
  if (!Number.isFinite(pageSize) || pageSize <= 0) return count;
  return Math.ceil(count / pageSize) * pageSize;
}

export interface CommentRevealPlan {
  /** 대상 댓글 id */
  targetId: string;
  /** 대상이 속한 루트 댓글 id (루트 대상이면 `targetId` 와 같다) */
  rootId: string;
  /** 루트 댓글의 카테고리. `categoryOf` 를 주지 않은(탭 없는) 섹션이면 `null` */
  category: CommentCategory | null;
  /** 같은 카테고리로 필터한 루트 목록 안에서의 인덱스 (탭 없는 섹션은 전체 루트 기준, 0-base) */
  rootIndex: number;
  /** 루트가 보이려면 필요한 최소 `visibleCount` — `rootIndex + 1` 을 `pageSize` 배수로 올림 */
  minVisibleCount: number;
  /** 루트부터 대상의 부모까지의 id (대상 제외). 답글이 아니면 빈 배열 */
  ancestorIds: string[];
  /** 대상이 소프트 삭제된 댓글인지. 삭제돼도 "삭제된 댓글입니다" 자리로 이동·강조한다(JCG-3603 결정) */
  isTargetDeleted: boolean;
}

export interface PlanCommentRevealParams {
  /** 섹션이 들고 있는 루트 댓글 배열 (표시 순서 그대로) */
  roots: Comment[];
  targetId: string;
  /**
   * 루트 댓글 → 탭 카테고리. 섹션의 필터 로직과 **같은 함수**를 넘겨야 한다
   * (예: `CommentSection` 은 `category || "chat"`, `ForumCommentSection` 은 `category || "question"`).
   * 탭이 없는 섹션(복원 게시판)은 생략한다.
   */
  categoryOf?: (comment: Comment) => CommentCategory;
  /** 섹션의 "더보기" 단위(`COMMENTS_PER_PAGE`) */
  pageSize: number;
}

/**
 * 대상 댓글이 화면에 존재하려면 섹션이 어느 탭을 열고 `visibleCount` 를 얼마까지 늘려야 하는지 계산한다.
 *
 * - 답글의 카테고리는 무시하고 **루트의 카테고리**로 탭을 정한다(섹션이 루트만 필터하기 때문).
 * - 대상이 트리에 없으면 `null` — 호출자는 아무 것도 하지 않는다(부재·다른 slug 폴백).
 */
export function planCommentReveal({
  roots,
  targetId,
  categoryOf,
  pageSize,
}: PlanCommentRevealParams): CommentRevealPlan | null {
  const path = findCommentPath(roots, targetId);
  if (!path) return null;

  const category = categoryOf ? categoryOf(path.root) : null;
  const siblings = categoryOf
    ? roots.filter((comment) => categoryOf(comment) === category)
    : roots;
  const rootIndex = siblings.indexOf(path.root);
  if (rootIndex < 0) return null;

  return {
    targetId,
    rootId: path.root.id,
    category,
    rootIndex,
    minVisibleCount: roundUpToPageSize(rootIndex + 1, pageSize),
    ancestorIds: path.ancestorIds,
    isTargetDeleted: path.target.isDeleted === true,
  };
}
